import type { ReviewItem } from "../../api/types";
import { t } from "../../i18n/t";

/**
 * A plain title for a review item, so the table does not lead with a raw key such as
 * `FX_OPENING: fx_opening_balance_missing:a063519f-...`. Only keys whose meaning is certain are mapped; an
 * unknown key returns `null` and the caller falls back to the item's own one-line summary (never to the key).
 *
 * Each entry translates at the call site so the "every key is referenced" i18n check can see the reference.
 */
const TITLES: Readonly<Record<string, () => string>> = {
  fx_opening_balance_missing: () => t("review.title_fx_opening_balance_missing"),
  fx_opening_balance_estimated: () => t("review.title_fx_opening_balance_estimated"),
  fixed_asset_depreciation_not_aligned: () => t("review.title_fixed_asset_depreciation_not_aligned"),
  fixed_asset_notes: () => t("review.title_fixed_asset_notes"),
  journals_scope_not_granted: () => t("review.title_journals_scope_not_granted"),
  projects_scope_not_granted: () => t("review.title_projects_scope_not_granted"),
  fx_convention_unverified: () => t("review.title_fx_convention_unverified"),
  items_tracked_not_migrated: () => t("review.title_items_tracked_not_migrated"),
  quotes_not_migrated: () => t("review.title_quotes_not_migrated"),
  repeating_invoices_not_migrated: () => t("review.title_repeating_invoices_not_migrated"),
  documents_zero_value_lines_dropped: () => t("review.title_documents_zero_value_lines_dropped"),
  documents_dropped_before_canonical: () => t("review.title_documents_dropped_before_canonical"),
  depreciation_source_type_unpinned: () => t("review.title_depreciation_source_type_unpinned"),
  fixed_assets_non_gbp: () => t("review.title_fixed_assets_non_gbp"),
  history_from_not_fy_aligned: () => t("review.title_history_from_not_fy_aligned"),
};

/** The key's family: the text before the first `:` (`fx_opening_balance_missing:<account guid>` -> the family). */
function family(key: string): string {
  const cut = key.indexOf(":");
  return cut === -1 ? key : key.slice(0, cut);
}

/** The account an FX opening-balance item is about, read from its message ("BANK-A0635 Wise - TRY"). */
function accountOf(item: Pick<ReviewItem, "key" | "message">): string | null {
  const fam = family(item.key);
  if (fam === "fx_opening_balance_missing") {
    return /^(.+?) is a foreign-currency bank account/.exec(item.message)?.[1] ?? null;
  }
  if (fam === "fx_opening_balance_estimated") {
    return /\bfor (.+?); LuciBook will\b/.exec(item.message)?.[1] ?? null;
  }
  return null;
}

export type ItemTitle = {
  /** The plain title, or `null` when the key is not one we can name (use the summary instead). */
  title: string | null;
  /** The account name the item refers to, when it refers to one and the message names it. */
  account: string | null;
};

export function itemTitle(item: Pick<ReviewItem, "key" | "message">): ItemTitle {
  const named = TITLES[family(item.key)];
  return { title: named === undefined ? null : named(), account: accountOf(item) };
}

const OPTION_LABELS: Readonly<Record<string, () => string>> = {
  acknowledge: () => t("review.option_acknowledge"),
  accept_estimate: () => t("review.option_accept_estimate"),
};

/** A plain label for a known answer; any other option shows as the producer wrote it. */
export function optionLabel(option: string): string {
  return OPTION_LABELS[option]?.() ?? option;
}
